<template>
  <div class="mt-3">
    <div class="card flat-card mb-3">
      <div class="card-body p-3">
        <h6 class="section-title mb-3">
          <i class="bi bi-megaphone-fill mr-1 text-primary"></i> Send Blast / SMS
        </h6>
        <p class="text-secondary mb-3" style="font-size:.82rem;">
          <i class="bi bi-shield-check mr-1"></i>
          Nothing is sent straight away. Each request is posted to Slack and only goes out once it's approved there.
        </p>

        <form @submit.prevent="submit">
          <div class="form-group">
            <label class="font-weight-bold d-block">Send as</label>
            <div class="btn-group" role="group">
              <button type="button" class="btn" :class="form.channel === 'notification' ? 'btn-primary' : 'btn-outline-secondary'" @click="setChannel('notification')">
                <i class="bi bi-bell-fill mr-1"></i> In-app notification
              </button>
              <button type="button" class="btn" :class="form.channel === 'sms' ? 'btn-primary' : 'btn-outline-secondary'" @click="setChannel('sms')">
                <i class="bi bi-chat-dots-fill mr-1"></i> SMS
              </button>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group col-md-6">
              <label class="font-weight-bold">Send to</label>
              <select v-model="form.audience" class="custom-select">
                <option v-for="a in audienceOptions" :key="a.value" :value="a.value">{{ a.label }}</option>
              </select>
            </div>
            <div v-if="form.channel === 'notification'" class="form-group col-md-6">
              <label class="font-weight-bold">Priority</label>
              <select v-model="form.priority" class="custom-select">
                <option value="normal">Normal: appears in notification list</option>
                <option value="crucial">Crucial: pops up on top of page</option>
              </select>
            </div>
          </div>

          <div v-if="form.audience === 'custom'" class="form-group">
            <label class="font-weight-bold">Phone numbers</label>
            <textarea
              v-model="form.numbers"
              class="form-control"
              rows="4"
              placeholder="Paste numbers separated by spaces, commas or new lines, e.g. 0712345678, 254722000111"
            ></textarea>
          </div>

          <p class="mb-3" style="font-size:.85rem;">
            <span v-if="previewing" class="text-secondary"><span class="spinner-border spinner-border-sm mr-1"></span> Counting recipients…</span>
            <template v-else-if="preview">
              <strong class="text-primary">{{ preview.count }}</strong>
              <span class="text-secondary">{{ preview.count === 1 ? 'recipient' : 'recipients' }}</span>
              <span v-if="preview.skipped" class="text-secondary"> &middot; {{ preview.skipped }} skipped (no phone number)</span>
              <span v-if="preview.invalid.length" class="text-danger d-block mt-1">
                <i class="bi bi-exclamation-circle mr-1"></i>
                {{ preview.invalid.length }} invalid: {{ preview.invalid.slice(0, 10).join(', ') }}<span v-if="preview.invalid.length > 10">…</span>
              </span>
            </template>
          </p>

          <div v-if="form.channel === 'notification'" class="form-group">
            <label class="font-weight-bold">Title</label>
            <input v-model="form.title" class="form-control" placeholder="System maintenance scheduled..." required maxlength="150" />
          </div>

          <div class="form-group">
            <label class="font-weight-bold">Message</label>
            <textarea
              v-model="form.body"
              class="form-control"
              rows="3"
              :maxlength="form.channel === 'sms' ? 480 : 1000"
              required
              :placeholder="form.channel === 'sms' ? 'Type the SMS...' : 'Describe the notification...'"
            ></textarea>
            <small v-if="form.channel === 'sms'" class="text-secondary">
              {{ form.body.length }}/480 characters &middot; {{ smsParts }} SMS per recipient
            </small>
          </div>

          <div v-if="form.channel === 'notification'" class="form-row">
            <div class="form-group col-md-8">
              <label class="font-weight-bold">Link URL <small class="text-secondary">(optional)</small></label>
              <input v-model="form.url" class="form-control" placeholder="https://..." maxlength="500" />
            </div>
            <div class="form-group col-md-4">
              <label class="font-weight-bold">Action Label <small class="text-secondary">(optional)</small></label>
              <input v-model="form.action" class="form-control" placeholder="View Details" maxlength="50" />
            </div>
          </div>

          <button class="btn btn-primary" type="submit" :disabled="sending || !canSubmit">
            <span v-if="sending" class="spinner-border spinner-border-sm mr-1"></span>
            <i v-else class="bi bi-send-fill mr-1"></i>
            Send for approval
          </button>
        </form>
      </div>
    </div>

    <div v-if="result" :class="['alert', result.ok ? 'alert-primary' : 'alert-danger']" style="font-size:.85rem;">
      <i :class="result.ok ? 'bi bi-check-circle mr-1' : 'bi bi-x-circle mr-1'"></i>
      {{ result.message }}
    </div>

    <div class="card flat-card">
      <div class="card-body p-3">
        <h6 class="section-title mb-3">Recent requests</h6>
        <p v-if="!history.length" class="text-secondary mb-0" style="font-size:.85rem;">No requests yet.</p>
        <div v-for="b in history" :key="b.id" class="history-row">
          <div class="d-flex align-items-center">
            <i class="bi mr-2 text-primary" :class="b.channel === 'sms' ? 'bi-chat-dots-fill' : 'bi-bell-fill'"></i>
            <strong class="text-truncate" style="font-size:.88rem;">{{ b.title || b.body }}</strong>
            <span class="badge ml-auto" :class="statusClass(b.status)">{{ b.status }}</span>
          </div>
          <small class="text-secondary">
            {{ b.reference_code }} &middot; {{ audienceLabel(b.audience) }} &middot; {{ b.recipient_count }} recipients
            <span v-if="b.status === 'sent' || b.status === 'failed'"> &middot; {{ b.sent_count }} sent<span v-if="b.failed_count">, {{ b.failed_count }} failed</span></span>
            &middot; {{ fromNow(b.created_at) }}
          </small>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

const AUDIENCES = [
  { value: 'all', label: 'All users' },
  { value: 'admins', label: 'All admins' },
  { value: 'owners', label: 'Business owners' },
  { value: 'non_admins', label: 'Non-admin users' },
  { value: 'affiliates', label: 'Affiliates' },
  { value: 'custom', label: 'Specific numbers (paste)', smsOnly: true },
];

const emptyForm = () => ({
  channel: 'notification',
  audience: 'all',
  numbers: '',
  title: '',
  body: '',
  priority: 'normal',
  url: '',
  action: '',
});

export default {
  name: 'AdminBlastNotification',

  data() {
    return {
      sending: false,
      result: null,
      form: emptyForm(),
      preview: null,
      previewing: false,
      previewTimer: null,
      history: [],
    };
  },

  computed: {
    audienceOptions() {
      return AUDIENCES.filter(a => !a.smsOnly || this.form.channel === 'sms');
    },
    smsParts() {
      return Math.max(1, Math.ceil(this.form.body.length / 160));
    },
    canSubmit() {
      return this.preview && this.preview.count > 0 && !this.previewing;
    },
    previewKey() {
      return [this.form.channel, this.form.audience, this.form.audience === 'custom' ? this.form.numbers : ''].join('|');
    },
  },

  watch: {
    previewKey: {
      immediate: true,
      handler() {
        clearTimeout(this.previewTimer);
        this.previewTimer = setTimeout(this.loadPreview, 400);
      },
    },
  },

  mounted() {
    this.loadHistory();
  },

  beforeDestroy() {
    clearTimeout(this.previewTimer);
  },

  methods: {
    setChannel(channel) {
      this.form.channel = channel;
      if (channel === 'notification' && this.form.audience === 'custom') this.form.audience = 'all';
    },

    async loadPreview() {
      if (this.form.audience === 'custom' && !this.form.numbers.trim()) {
        this.preview = { count: 0, skipped: 0, invalid: [] };
        return;
      }
      this.previewing = true;
      try {
        const { data } = await axios.post('/api/admin/broadcasts/preview', {
          channel: this.form.channel,
          audience: this.form.audience,
          numbers: this.form.audience === 'custom' ? this.form.numbers : null,
        }, { _silent: true });
        this.preview = data;
      } catch (e) {
        this.preview = null;
      } finally {
        this.previewing = false;
      }
    },

    async loadHistory() {
      try {
        const { data } = await axios.get('/api/admin/broadcasts', { _silent: true });
        this.history = data;
      } catch (e) {
        this.history = [];
      }
    },

    async submit() {
      this.sending = true;
      this.result = null;
      try {
        const sms = this.form.channel === 'sms';
        const { data } = await axios.post('/api/admin/broadcasts', {
          channel: this.form.channel,
          audience: this.form.audience,
          numbers: this.form.audience === 'custom' ? this.form.numbers : null,
          title: sms ? null : this.form.title,
          body: this.form.body,
          priority: sms ? 'normal' : this.form.priority,
          url: sms ? null : (this.form.url || null),
          action: sms ? null : (this.form.action || null),
        });
        this.result = {
          ok: true,
          message: `Sent for approval (${data.reference_code}, ${data.recipient_count} recipients). It goes out once approved on Slack.`,
        };
        this.form = { ...emptyForm(), channel: this.form.channel };
        this.loadHistory();
      } catch (e) {
        this.result = { ok: false, message: e?.response?.data?.message || 'Failed to submit.' };
      } finally {
        this.sending = false;
      }
    },

    audienceLabel(value) {
      const a = AUDIENCES.find(x => x.value === value);
      return a ? a.label.replace(' (paste)', '') : value;
    },

    statusClass(status) {
      return {
        pending: 'badge-secondary',
        approved: 'badge-primary',
        sending: 'badge-primary',
        sent: 'badge-dark',
        rejected: 'badge-danger',
        failed: 'badge-danger',
      }[status] || 'badge-secondary';
    },

    fromNow(ts) {
      return typeof moment !== 'undefined' ? moment(ts).fromNow() : ts;
    },
  },
};
</script>

<style scoped>
.flat-card {
  border: 1px solid #e9ecef;
  border-radius: 14px;
}
.section-title {
  color: #0f172a;
  font-weight: 600;
}
.history-row {
  padding: 8px 0;
  border-bottom: 1px solid #f1f3f5;
}
.history-row:last-child {
  border-bottom: none;
}
</style>
